import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const typeMeta = {
  public_holiday: { label: "Public Holiday", dot: "bg-yellow-400", chip: "bg-yellow-100 text-yellow-800" },
  rdo: { label: "Construction RDO", dot: "bg-orange-500", chip: "bg-orange-100 text-orange-700" },
};

export default function CalendarSettings() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ date: "", type: "public_holiday", name: "" });
  const [saving, setSaving] = useState(false);
  const year = new Date().getFullYear();

  const load = () => api.get("/calendar-events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.date) { toast.error("Pick a date"); return; }
    setSaving(true);
    try { await api.post("/calendar-events", form); toast.success("Date added"); setForm({ date: "", type: form.type, name: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Remove this date?")) return; await api.delete(`/calendar-events/${id}`); toast.success("Removed"); load(); };

  const years = [...new Set(events.map((e) => e.year))].sort();

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy flex items-center gap-2"><CalendarClock className="w-7 h-7" />Holidays &amp; RDOs</h1>
        <p className="text-slate-500 mt-1">Victorian Public Holidays &amp; Construction Industry RDOs used for scheduling warnings. Update annually — no code changes needed.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <h3 className="font-heading font-semibold text-navy mb-3">Add a date</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div><Label>Date</Label><Input data-testid="event-date-input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="mt-1" /></div>
          <div><Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger data-testid="event-type-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public_holiday">Public Holiday</SelectItem>
                <SelectItem value="rdo">Construction RDO</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Name / Label</Label><Input data-testid="event-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1" placeholder="e.g. Melbourne Cup Day" /></div>
          <Button data-testid="event-add-button" onClick={add} disabled={saving} className="bg-navy hover:bg-navy-light"><Plus className="w-4 h-4 mr-2" />Add</Button>
        </div>
      </div>

      {years.map((y) => (
        <div key={y}>
          <h3 className="font-heading font-semibold text-navy mb-2">{y}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {events.filter((e) => e.year === y).map((e) => {
              const m = typeMeta[e.type] || typeMeta.public_holiday;
              return (
                <div key={e.id} data-testid={`event-row-${e.id}`} className="flex items-center gap-3 bg-white rounded-xl border border-slate-100 p-3 shadow-sm">
                  <span className={`w-2.5 h-2.5 rounded-full ${m.dot} shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy">{new Date(e.date).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}</p>
                    <p className="text-xs text-slate-500 truncate">{e.name}</p>
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${m.chip}`}>{m.label}</span>
                  <button data-testid={`event-delete-${e.id}`} onClick={() => del(e.id)} className="text-red-500 p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {events.length === 0 && <p className="text-slate-400 text-center py-12">No dates yet.</p>}
    </div>
  );
}
